import { ActorType, AuditPolicy, AuthenticationAssuranceLevel, ConcurrencyPolicy, IdempotencyPolicy, PrivacyClassification, ScopeType, } from './operation.js';
export const PLATFORM_ENTITLEMENT_ASSERTION_ISSUE = 'platform.integration.entitlements.issue';
export const PLATFORM_REFERENCE_DATA_ASSERTION_ISSUE = 'platform.integration.reference-data.issue';
export const PLATFORM_SUPPORT_DELEGATION_ASSERTION_ISSUE = 'platform.integration.support-delegation.issue';
export const PLATFORM_RUNTIME_HEARTBEAT_INGEST = 'platform.integration.runtime-heartbeat.ingest';
export const PLATFORM_USAGE_STATEMENT_INGEST = 'platform.integration.usage-statement.ingest';
export const PLATFORM_BOOTSTRAP_ATTESTATION_INGEST = 'platform.integration.bootstrap-attestation.ingest';
export const PLATFORM_SUPPORT_COMMANDS_READ = 'platform.integration.support-commands.read';
export const PLATFORM_SUPPORT_COMMAND_ACKNOWLEDGE = 'platform.integration.support-command.acknowledge';
export const PLATFORM_REFERENCE_DATA_RELEASE_CREATE = 'platform.reference-data.release.create';
export const PLATFORM_REFERENCE_DATA_RELEASE_PUBLISH = 'platform.reference-data.release.publish';
function readAssertionOperation(id, name, permission) {
    return {
        id,
        name,
        labelKey: `operations.${id}.label`,
        descriptionKey: `operations.${id}.description`,
        permission,
        permissionId: permission,
        module: 'PLATFORM',
        requiredModules: ['PLATFORM'],
        requiresEntitledModule: false,
        requiredAal: AuthenticationAssuranceLevel.AAL1,
        scopeTypes: [ScopeType.PLATFORM],
        allowedScopeKinds: ['PLATFORM', 'SYSTEM'],
        requestedScope: {},
        forcesClientScope: false,
        makerCheckerRequired: false,
        idempotencyPolicy: IdempotencyPolicy.NOT_APPLICABLE,
        concurrencyPolicy: ConcurrencyPolicy.NONE,
        auditPolicy: AuditPolicy.SENSITIVE,
        privacyClassification: PrivacyClassification.CONFIDENTIAL,
        telemetryId: id,
        allowedActorTypes: [ActorType.USER, ActorType.SERVICE_ACCOUNT],
        httpMethods: ['GET'],
        isReadOnly: true,
        isFinancial: false,
        supportDelegationEligible: false,
        ownershipPolicy: 'PLATFORM',
        delegationPolicy: 'NOT_ALLOWED',
        clientForcing: 'NONE',
        accessType: 'READ',
    };
}
function machineIntegrationOperation(id, name, method) {
    const readOnly = method === 'GET';
    return {
        id,
        name,
        labelKey: `operations.${id}.label`,
        descriptionKey: `operations.${id}.description`,
        // Machine endpoints are authenticated by the dedicated, installation-bound
        // service-account scope guard. The platform permission here remains a
        // canonical metadata/telemetry classification, not a substitute for it.
        permission: 'platform:organizations:read',
        permissionId: 'platform:organizations:read',
        module: 'PLATFORM',
        requiredModules: ['PLATFORM'],
        requiresEntitledModule: false,
        requiredAal: AuthenticationAssuranceLevel.AAL1,
        scopeTypes: [ScopeType.PLATFORM],
        allowedScopeKinds: ['PLATFORM', 'SYSTEM'],
        requestedScope: {},
        forcesClientScope: false,
        makerCheckerRequired: false,
        idempotencyPolicy: readOnly ? IdempotencyPolicy.NOT_APPLICABLE : IdempotencyPolicy.REQUIRED,
        concurrencyPolicy: ConcurrencyPolicy.NONE,
        auditPolicy: AuditPolicy.SENSITIVE,
        privacyClassification: PrivacyClassification.CONFIDENTIAL,
        telemetryId: id,
        allowedActorTypes: [ActorType.SERVICE_ACCOUNT],
        httpMethods: [method],
        isReadOnly: readOnly,
        isFinancial: false,
        supportDelegationEligible: false,
        ownershipPolicy: 'PLATFORM',
        delegationPolicy: 'NOT_ALLOWED',
        clientForcing: 'NONE',
        accessType: readOnly ? 'READ' : 'WRITE',
    };
}
function writeReleaseOperation(id, name, makerCheckerRequired, concurrencyPolicy) {
    return {
        id,
        name,
        labelKey: `operations.${id}.label`,
        descriptionKey: `operations.${id}.description`,
        permission: 'platform:reference-master:manage',
        permissionId: 'platform:reference-master:manage',
        module: 'PLATFORM',
        requiredModules: ['PLATFORM'],
        requiresEntitledModule: false,
        requiredAal: AuthenticationAssuranceLevel.AAL2,
        scopeTypes: [ScopeType.PLATFORM],
        allowedScopeKinds: ['PLATFORM', 'SYSTEM'],
        requestedScope: {},
        forcesClientScope: false,
        makerCheckerRequired,
        idempotencyPolicy: IdempotencyPolicy.OPTIONAL,
        concurrencyPolicy,
        auditPolicy: AuditPolicy.SENSITIVE,
        privacyClassification: PrivacyClassification.CONFIDENTIAL,
        telemetryId: id,
        allowedActorTypes: [ActorType.USER],
        httpMethods: ['POST'],
        isReadOnly: false,
        isFinancial: false,
        supportDelegationEligible: false,
        ownershipPolicy: 'PLATFORM',
        delegationPolicy: 'NOT_ALLOWED',
        clientForcing: 'NONE',
        accessType: 'ADMIN',
    };
}
export const PLATFORM_INTEGRATION_OPERATIONS = {
    [PLATFORM_ENTITLEMENT_ASSERTION_ISSUE]: readAssertionOperation(PLATFORM_ENTITLEMENT_ASSERTION_ISSUE, 'Issue Organization Entitlement Assertion', 'platform:organizations:read'),
    [PLATFORM_REFERENCE_DATA_ASSERTION_ISSUE]: readAssertionOperation(PLATFORM_REFERENCE_DATA_ASSERTION_ISSUE, 'Issue Reference Data Release Assertion', 'platform:reference-master:read'),
    [PLATFORM_SUPPORT_DELEGATION_ASSERTION_ISSUE]: readAssertionOperation(PLATFORM_SUPPORT_DELEGATION_ASSERTION_ISSUE, 'Issue Support Delegation Assertion', 'platform:support-delegations:read'),
    [PLATFORM_RUNTIME_HEARTBEAT_INGEST]: machineIntegrationOperation(PLATFORM_RUNTIME_HEARTBEAT_INGEST, 'Ingest Organization Runtime Heartbeat', 'POST'),
    [PLATFORM_USAGE_STATEMENT_INGEST]: machineIntegrationOperation(PLATFORM_USAGE_STATEMENT_INGEST, 'Ingest Organization Usage Statement', 'POST'),
    [PLATFORM_BOOTSTRAP_ATTESTATION_INGEST]: machineIntegrationOperation(PLATFORM_BOOTSTRAP_ATTESTATION_INGEST, 'Ingest Organization Bootstrap Completion Attestation', 'POST'),
    [PLATFORM_SUPPORT_COMMANDS_READ]: machineIntegrationOperation(PLATFORM_SUPPORT_COMMANDS_READ, 'Read Organization Support Commands', 'GET'),
    [PLATFORM_SUPPORT_COMMAND_ACKNOWLEDGE]: machineIntegrationOperation(PLATFORM_SUPPORT_COMMAND_ACKNOWLEDGE, 'Acknowledge Organization Support Command', 'POST'),
    [PLATFORM_REFERENCE_DATA_RELEASE_CREATE]: writeReleaseOperation(PLATFORM_REFERENCE_DATA_RELEASE_CREATE, 'Create Reference Data Release Draft', false, ConcurrencyPolicy.NONE),
    [PLATFORM_REFERENCE_DATA_RELEASE_PUBLISH]: writeReleaseOperation(PLATFORM_REFERENCE_DATA_RELEASE_PUBLISH, 'Publish Reference Data Release', true, ConcurrencyPolicy.OPTIMISTIC_VERSION),
};
